import { useCallback, useEffect, useMemo, useState } from "react";
import { api, setAuthToken, setDevIdentity, type BoardMeta } from "./api/client";
import { startSession, type Session } from "./discord/session";
import { connectBoard, type BoardConnection } from "./sync/provider";
import { connectLobby, type JoinRequest, type LiveBoard, type LobbyConnection } from "./sync/lobby";
import { BoardContext } from "./sync/BoardContext";
import { BoardView } from "./components/BoardView";
import { Landing } from "./components/Landing";
import { Splash } from "./components/Splash";
import { useDialogs } from "./ui/dialogs";

export function App() {
  const dialogs = useDialogs();
  const [session, setSession] = useState<Session | null>(null);
  const [mine, setMine] = useState<BoardMeta[]>([]);
  const [shared, setShared] = useState<BoardMeta[]>([]);
  const [liveBoards, setLiveBoards] = useState<LiveBoard[]>([]);
  const [requests, setRequests] = useState<Record<string, JoinRequest[]>>({});
  const [lobby, setLobby] = useState<LobbyConnection | null>(null);
  const [boardId, setBoardId] = useState<string | null>(null);
  const [connection, setConnection] = useState<BoardConnection | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshBoards = useCallback(async () => {
    const lists = await api.listBoards();
    setMine(lists.mine);
    setShared(lists.shared);
    return lists;
  }, []);

  useEffect(() => {
    let cancelled = false;
    startSession()
      .then(async (next) => {
        if (cancelled) return;
        setAuthToken(next.accessToken);
        if (!next.inDiscord) setDevIdentity({ id: next.user.id, name: next.user.name });
        setSession(next);
        await refreshBoards();
      })
      .catch((cause) => setError(cause instanceof Error ? cause.message : String(cause)));
    return () => {
      cancelled = true;
    };
  }, [refreshBoards]);

  const openBoard = useCallback(
    async (id: string) => {
      try {
        const { board: fresh } = await api.getBoard(id);
        setMine((current) => current.map((item) => (item.id === id ? fresh : item)));
        setShared((current) => {
          const known = current.some((item) => item.id === id);
          if (known) return current.map((item) => (item.id === id ? fresh : item));
          return fresh.role === "owner" ? current : [...current, fresh];
        });
        if (fresh.role === "owner" && !mine.some((item) => item.id === id)) {
          setMine((current) => [fresh, ...current]);
        }
        setBoardId(id);
      } catch {
        dialogs.toast("Kein Zugriff auf dieses Board");
      }
    },
    [dialogs, mine],
  );

  // Channel-wide lobby: who is sharing what, and who wants in.
  useEffect(() => {
    if (!session) return;
    const connectionToLobby = connectLobby(session.channelId, session.user, {
      onLive: setLiveBoards,
      onRequests: (board, list) => setRequests((current) => ({ ...current, [board]: list })),
      onJoinResult: (board, approved) => {
        if (approved) {
          dialogs.toast("Beitritt bestätigt");
          void openBoard(board);
        } else {
          dialogs.toast("Beitritt abgelehnt");
        }
      },
      onSessionEnded: (board) => {
        setBoardId((current) => {
          if (current !== board) return current;
          dialogs.toast("Die Session wurde beendet");
          return null;
        });
      },
    });
    setLobby(connectionToLobby);
    return () => {
      connectionToLobby.close();
      setLobby(null);
    };
  }, [session, dialogs, openBoard]);

  useEffect(() => {
    if (!session || !boardId) return;
    const next = connectBoard(boardId, session.user);
    setConnection(next);
    setConnected(false);
    const onStatus = ({ status }: { status: string }) => setConnected(status === "connected");
    next.provider.on("status", onStatus);
    return () => {
      next.provider.off("status", onStatus);
      next.destroy();
      setConnection(null);
    };
  }, [session, boardId]);

  const board = useMemo(
    () => [...mine, ...shared].find((item) => item.id === boardId) ?? null,
    [mine, shared, boardId],
  );

  if (error) return <Splash title="Fehler" detail={error} />;
  if (!session) return <Splash title="Worktools" detail="Verbinde mit Discord…" />;

  if (!boardId || !board || !connection) {
    return (
      <Landing
        session={session}
        mine={mine}
        shared={shared}
        liveBoards={liveBoards}
        onOpenBoard={openBoard}
        onRequestJoin={(id) => lobby?.requestJoin(id)}
        onBoardsChanged={refreshBoards}
      />
    );
  }

  return (
    <BoardContext.Provider
      value={{
        doc: connection.doc,
        awareness: connection.provider.awareness,
        board,
        me: session.user,
        connected,
      }}
    >
      <BoardView
        key={board.id}
        session={session}
        liveBoards={liveBoards}
        joinRequests={requests[board.id] ?? []}
        lobby={lobby}
        onLeaveBoard={() => setBoardId(null)}
        onBoardsChanged={refreshBoards}
      />
    </BoardContext.Provider>
  );
}
